import React, { type FC, type PropsWithChildren, Suspense, useCallback, useEffect, useMemo } from 'react';
import { Divider, Form, InlineBanner } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import {
    AudioElement,
    ElementUpdateTypes,
    GroupElement,
    ImageElement,
    ShapeElement,
    TextElement,
    VideoElement,
} from '@bynder-studio/render-core';
import { Section, SidebarWrapper } from '../../index.styled';
import ElementAlignment from '../ElementAlignment';
import AllowToggleVisibility from '../AllowToggleVisibility';
import PersonalUploads from '../PersonalUploads';
import { flattenTree } from '~/common/editor/helpers/elementtree';
import useAccessRights from 'packages/hooks/useAccessRights';
import features from '~/configs/features';
import ContentProperty from '../ContentProperty';
import AssetSourceMultiple from '../AssetSource/AssetSourceMultiple';
import useForceUpdate from '~/hooks/useForceUpdate';

type SelectableElement = ImageElement | VideoElement | AudioElement | TextElement | ShapeElement;

const isControllableElement = (element: SelectableElement): element is ImageElement | VideoElement | AudioElement =>
    element instanceof ImageElement || element instanceof VideoElement || element instanceof AudioElement;

const WithDivider: FC<PropsWithChildren> = ({ children }) => {
    const subElements: React.ReactNode[] = [];
    const isEmpty = (obj: React.ReactNode) => obj === undefined || obj === null || Object.keys(obj).length === 0;

    React.Children.forEach(children, (child, index) => {
        if (isEmpty(child)) {
            return;
        }

        subElements.push(child);
        subElements.push(<Divider key={`divider-${index}`} />);
    });

    if (subElements.length > 1) {
        subElements.pop();
    }

    return <>{subElements}</>;
};

const MultiSelection = ({ creativeModel, selectedElements }) => {
    const { translate } = useTranslate();
    const { isPlatformAllowed } = useAccessRights();
    const forceUpdate = useForceUpdate();

    const elements = useMemo<SelectableElement[]>(
        () =>
            Object.values(flattenTree(selectedElements)).filter(
                (element): element is SelectableElement => !(element instanceof GroupElement),
            ),
        [selectedElements],
    );

    const controllableElements = useMemo(() => elements.filter(isControllableElement), [elements]);
    const nonAudioElements = useMemo(
        () => elements.filter((element) => !(element instanceof AudioElement)),
        [elements],
    );

    const hasControllableElement = controllableElements.length > 0;

    const allAudioSelection = useMemo(
        () => elements.every((element) => element instanceof AudioElement),
        [selectedElements],
    );

    const allLocked = elements.every((element) => element.locked);
    const hasLockedElements = elements.some((element) => element.locked);

    const defaultProps = {
        creativeModel,
        disabled: false,
    };

    const controllableElementsProps = {
        ...defaultProps,
        selectedElements: controllableElements,
    };

    const handleLockChangeAction = useCallback(
        ({ updateTypes, element }) => {
            if (updateTypes.has(ElementUpdateTypes.LOCK) && selectedElements.some((el) => el.id === element.id)) {
                forceUpdate();
            }
        },
        [selectedElements, forceUpdate],
    );

    const isMultiSelectionEditingAllowed = isPlatformAllowed([features.MULTI_SELECTION_EDITING]);

    useEffect(() => {
        if (!creativeModel) {
            return;
        }

        creativeModel.on('elementUpdated', handleLockChangeAction);

        return () => creativeModel.off('elementUpdated', handleLockChangeAction);
    }, [creativeModel]);

    return (
        <SidebarWrapper>
            <WithDivider>
                {isMultiSelectionEditingAllowed && !allLocked && hasLockedElements && (
                    <Section>
                        <InlineBanner variant="warning">
                            {translate('editor.element.multiselect.locked_elements_warning')}
                        </InlineBanner>
                    </Section>
                )}
                {!allAudioSelection && (
                    <Section aria-labelledby="shots-multi-selection-settings">
                        <ElementAlignment {...defaultProps} selectedElements={nonAudioElements} />
                    </Section>
                )}
                {isMultiSelectionEditingAllowed && isPlatformAllowed([features.CONTENT_PROPERTIES]) && (
                    <Section aria-labelledby="shots-text-content-property">
                        <Suspense>
                            <ContentProperty {...defaultProps} selectedElements={elements} />
                        </Suspense>
                    </Section>
                )}
                {isMultiSelectionEditingAllowed && hasControllableElement && (
                    <Section aria-labelledby="shots-asset-source">
                        <AssetSourceMultiple creativeModel={creativeModel} selectedElements={controllableElements} />
                    </Section>
                )}
                {isMultiSelectionEditingAllowed && hasControllableElement && (
                    <Section aria-labelledby="shots-image-asset-source">
                        <Form onSubmit={preventSubmit}>
                            <Form.Group>
                                <PersonalUploads {...controllableElementsProps} />
                            </Form.Group>
                        </Form>
                    </Section>
                )}
                {isMultiSelectionEditingAllowed && (
                    <Section aria-labelledby="shots-image-visibility">
                        <Form onSubmit={preventSubmit}>
                            <Form.Group>
                                <Form.Label>{translate('editor.element.visibility_control')}</Form.Label>
                                <AllowToggleVisibility {...defaultProps} selectedElements={elements} />
                            </Form.Group>
                        </Form>
                    </Section>
                )}
            </WithDivider>
        </SidebarWrapper>
    );
};

export default MultiSelection;

function preventSubmit(e: React.FormEvent) {
    e.preventDefault();
}
